import React from "react";
import { Toaster } from "react-hot-toast";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ModeProvider, useMode } from "./contexts/ModeContext";

// Auth Components
import Login from "./components/Auth/Login";
import Register from "./components/Auth/Register";

// Dashboard Components
import SellerDashboard from "./components/Dashboard/SellerDashboard";
import AgentDashboard from "./components/Dashboard/AgentDashboard";
import AdminDashboard from "./components/Dashboard/AdminDashboard";

// Common Components
import Navbar from "./components/Common/Navbar";
import ProtectedRoute from "./components/Common/ProtectedRoute";
import LoadingSpinner from "./components/Common/LoadingSpinner";

// Material Components
import MaterialList from "./components/Materials/MaterialList";
import MaterialDetails from "./components/Materials/MaterialDetails";

// Bin Components
import Bin from "./components/Bin/Bin";

// Agent Components
import AgentList from "./components/Agents/AgentList";
import AgentDetails from "./components/Agents/AgentDetails";

// Bank Components
import BankList from "./components/Bank/BankList";

// Sell Components
import Sell from "./components/Sell/Sell";

// History Components
import SaleHistory from "./components/History/SaleHistory";

// Agent-specific Components
import RegisterAgent from "./components/Agent/RegisterAgent";
import AgentPendingValidations from "./components/Agent/PendingValidations";
import AgentValidationHistory from "./components/Agent/ValidationHistory";
import DeliveryHistory from "./components/Agent/DeliveryHistory";

// Admin Components
import MaterialAdmin from "./components/Admin/MaterialAdmin";
import UserSalesAdmin from "./components/Admin/UserSalesAdmin";
import AgentDeliveryAdmin from "./components/Admin/AgentDeliveryAdmin";
import RewardsAdmin from "./components/Admin/RewardsAdmin";
import BountyManagement from "./components/Admin/BountyManagement";
import RewardsManagement from "./components/Admin/RewardsManagement";
import ContactManagement from "./components/Admin/ContactManagement";
import NewsletterManagement from "./components/Admin/NewsletterManagement";
import MobileControl from "./components/Admin/MobileControl";
import SlideshowAdmin from "./components/Admin/SlideshowAdmin";
import PendingValidations from "./components/Admin/PendingValidations";
import ValidationHistory from "./components/Admin/ValidationHistory";
import UserManagementAdmin from "./components/Admin/UserManagementAdmin";
import AgentManagementAdmin from "./components/Admin/AgentManagementAdmin";

// Page Components
import AboutUs from "./components/Pages/AboutUs";
import OurMission from "./components/Pages/OurMission";
import Careers from "./components/Pages/Careers";
import ContactUs from "./components/Pages/ContactUs";
import PrivacyPolicy from "./components/Pages/PrivacyPolicy";
import TermsOfService from "./components/Pages/TermsOfService";
import CookiePolicy from "./components/Pages/CookiePolicy";
import DataProtection from "./components/Pages/DataProtection";
import DownloadPage from "./components/Pages/DownloadPage";

// Referral Components
import Referrals from "./components/Referrals/Referrals";

// Notification Components
import Notifications from "./components/Notifications/Notifications";

// Profile Components
import ProfilePage from "./components/Profile/ProfilePage";

// Rewards Components
import RewardsPage from "./components/Rewards/RewardsPage";

// Common Components
import Footer from "./components/Common/Footer";
import ScrollToTop from "./components/Common/ScrollToTop";
import AppSidebar from "./components/Common/AppSidebar";
import SupportPage from "./components/Support/SupportPage";
import OperationsAdmin from "./components/Admin/OperationsAdmin";
import PaymentsAdmin from "./components/Admin/PaymentsAdmin";
import LiveProvider from "./components/Live/LiveProvider";
import RealtimeSync from "./query/RealtimeSync";
import AnnouncementsAdmin from "./components/Admin/AnnouncementsAdmin";

// Referral Components
import ReferralLanding from "./components/Referral/ReferralLanding";
import PublicLanding from "./components/Pages/PublicLanding";

function AppContent() {
  const { loading, user, isAgent, isAdmin } = useAuth();
  const { isAgentMode } = useMode();

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <Router>
      <AppRouter />
    </Router>
  );
}

function AppRouter() {
  const location = useLocation();
  const isAuthPage =
    location.pathname.startsWith("/login") ||
    location.pathname.startsWith("/register");
  
  // Pages where footer should be hidden
  const hideFooterPaths = ['/referral/', '/login', '/register'];
  const shouldHideFooter = hideFooterPaths.some(path => location.pathname.startsWith(path));

  const { user, isAgent, isAdmin } = useAuth();
  const { isAgentMode } = useMode();

  return (
    <LiveProvider>
    <RealtimeSync />
    <div className="min-h-screen bg-gray-50">
      <Toaster
        position="top-center"
        toastOptions={{
          duration: 4000,
          style: { background: "#fff", color: "#111827", boxShadow: "0 10px 40px rgba(0,0,0,.12)" },
        }}
      />
      <ScrollToTop />
      {!isAuthPage && <Navbar />}
      <div className={!isAuthPage && user ? "lg:flex" : ""}>
      {!isAuthPage && user && <AppSidebar />}
      <main className={isAuthPage ? "min-h-screen flex-1" : "container mx-auto min-w-0 w-full px-4 py-8 min-h-screen flex-1"}>
        <Routes>
            {/* Public Routes */}
            <Route
              path="/login"
              element={!user ? <Login /> : <Navigate to="/" />}
            />
            <Route
              path="/register"
              element={!user ? <Register /> : <Navigate to="/" />}
            />

            {/* Protected Routes */}
            <Route
              path="/"
              element={
                !user ? (
                  <PublicLanding />
                ) : (
                  <ProtectedRoute>
                    {isAdmin ? (
                      <AdminDashboard />
                    ) : isAgent && isAgentMode ? (
                      <AgentDashboard />
                    ) : (
                      <SellerDashboard />
                    )}
                  </ProtectedRoute>
                )
              }
            />

            {/* Seller Routes */}
            <Route
              path="/materials"
              element={<MaterialList />}
            />
            <Route
              path="/materials/:id"
              element={<MaterialDetails />}
            />
            <Route
              path="/bin"
              element={<Bin />}
            />
            <Route
              path="/agents"
              element={<AgentList />}
            />
            <Route
              path="/agents/:id"
              element={<AgentDetails />}
            />
            <Route
              path="/banks"
              element={
                <ProtectedRoute>
                  <BankList />
                </ProtectedRoute>
              }
            />
            <Route
              path="/sell"
              element={
                <ProtectedRoute>
                  <Sell />
                </ProtectedRoute>
              }
            />
            <Route
              path="/sales"
              element={
                <ProtectedRoute>
                  <SaleHistory />
                </ProtectedRoute>
              }
            />
            <Route
              path="/referrals"
              element={
                <ProtectedRoute>
                  <Referrals />
                </ProtectedRoute>
              }
            />
            <Route
              path="/notifications"
              element={
                <ProtectedRoute>
                  <Notifications />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <ProfilePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/rewards"
              element={
                <ProtectedRoute>
                  <RewardsPage />
                </ProtectedRoute>
              }
            />

            {/* Agent Routes on */}
            <Route
              path="/agent/register"
              element={
                <ProtectedRoute>
                  <RegisterAgent />
                </ProtectedRoute>
              }
            />
            <Route
              path="/agent/pending-validations"
              element={
                <ProtectedRoute requireAgent>
                  <AgentPendingValidations />
                </ProtectedRoute>
              }
            />
            <Route
              path="/agent/validation-history"
              element={
                <ProtectedRoute requireAgent>
                  <AgentValidationHistory />
                </ProtectedRoute>
              }
            />
            <Route
              path="/agent/delivery-history"
              element={
                <ProtectedRoute requireAgent>
                  <DeliveryHistory />
                </ProtectedRoute>
              }
            />

            {/* Admin Routes */}
            <Route
              path="/admin/materials"
              element={
                <ProtectedRoute requireAdmin>
                  <MaterialAdmin />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/users"
              element={
                <ProtectedRoute requireAdmin>
                  <UserManagementAdmin />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/agents-manage"
              element={
                <ProtectedRoute requireAdmin>
                  <AgentManagementAdmin />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/sales"
              element={
                <ProtectedRoute requireAdmin>
                  <UserSalesAdmin />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/deliveries"
              element={
                <ProtectedRoute requireAdmin>
                  <AgentDeliveryAdmin />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/rewards"
              element={
                <ProtectedRoute requireAdmin>
                  <RewardsAdmin />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/bounties"
              element={
                <ProtectedRoute requireAdmin>
                  <BountyManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/rewards-management"
              element={
                <ProtectedRoute requireAdmin>
                  <RewardsManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/contact-management"
              element={
                <ProtectedRoute requireAdmin>
                  <ContactManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/newsletter-management"
              element={
                <ProtectedRoute requireAdmin>
                  <NewsletterManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/pending-validations"
              element={
                <ProtectedRoute requireAdmin>
                  <PendingValidations />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/validation-history"
              element={
                <ProtectedRoute requireAdmin>
                  <ValidationHistory />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/mobile-control"
              element={
                <ProtectedRoute requireAdmin>
                  <MobileControl />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/settings"
              element={
                <ProtectedRoute requireAdmin>
                  <SlideshowAdmin />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/slideshow"
              element={
                <ProtectedRoute requireAdmin>
                  <SlideshowAdmin />
                </ProtectedRoute>
              }
            />

            {/* Public Page Routes */}
            <Route path="/about" element={<AboutUs />} />
            <Route path="/mission" element={<OurMission />} />
            <Route path="/careers" element={<Careers />} />
            <Route path="/contact" element={<ContactUs />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/cookies" element={<CookiePolicy />} />
            <Route path="/data-protection" element={<DataProtection />} />
            <Route path="/referral/:referralId" element={<ReferralLanding />} />
            <Route path="/support" element={<ProtectedRoute><SupportPage /></ProtectedRoute>} />
            <Route path="/admin/operations" element={<ProtectedRoute requireAdmin><OperationsAdmin /></ProtectedRoute>} />
            <Route path="/admin/payments" element={<ProtectedRoute requireAdmin><PaymentsAdmin /></ProtectedRoute>} />
            <Route path="/admin/announcements" element={<ProtectedRoute requireAdmin><AnnouncementsAdmin /></ProtectedRoute>} />
            <Route path="/download" element={<DownloadPage />} />

            {/* Catch all route */}
            <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </main>
      </div>
      {!shouldHideFooter && <Footer />}
    </div>
    </LiveProvider>
  );
}

function App() {
  return (
    <AuthProvider>
      <ModeProvider>
        <AppContent />
      </ModeProvider>
    </AuthProvider>
  );
}

export default App;
